import React, { useState } from "react";
import { IconButton, InputAdornment } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import EventIcon from "@mui/icons-material/Event";
import * as S from "./styles";
import { revealChip } from "../../utils/globalStyles/chips";
import { TransactionType } from "../../utils/services/modules/transactions";
import { formatShortDay, todayInBrasilia } from "../../utils/formatDatas";

export type PeriodKey = "all" | "today" | "7d" | "30d" | "custom";

export interface ICustomRange {
  from: string; // YYYY-MM-DD ou ""
  to: string;
}

interface IStatementFilters {
  type: TransactionType;
  onTypeChange: (type: TransactionType) => void;
  period: PeriodKey;
  customRange: ICustomRange;
  onPeriodChange: (period: PeriodKey, customRange?: ICustomRange) => void;
  search: string;
  onSearchChange: (search: string) => void;
  hasActiveFilters: boolean;
  onClear: () => void;
}

const TYPE_OPTIONS: { value: TransactionType; label: string }[] = [
  { value: "all", label: "Todos" },
  { value: "sent", label: "Enviados" },
  { value: "received", label: "Recebidos" },
];

const PERIOD_OPTIONS: { value: Exclude<PeriodKey, "custom">; label: string }[] =
  [
    { value: "all", label: "Todo período" },
    { value: "today", label: "Hoje" },
    { value: "7d", label: "7 dias" },
    { value: "30d", label: "30 dias" },
  ];

const customLabel = ({ from, to }: ICustomRange) => {
  if (from && to) return `${formatShortDay(from)} – ${formatShortDay(to)}`;
  if (from) return `Desde ${formatShortDay(from)}`;
  if (to) return `Até ${formatShortDay(to)}`;
  return "Personalizado";
};

const StatementFilters: React.FC<IStatementFilters> = ({
  type,
  onTypeChange,
  period,
  customRange,
  onPeriodChange,
  search,
  onSearchChange,
  hasActiveFilters,
  onClear,
}) => {
  const [sheetOpen, setSheetOpen] = useState(false);
  const [draft, setDraft] = useState<ICustomRange>(customRange);
  const today = todayInBrasilia();

  const openSheet = () => {
    setDraft(customRange);
    setSheetOpen(true);
  };

  const applyCustom = () => {
    // se o usuário inverter as datas, corrige em vez de dar erro
    const range =
      draft.from && draft.to && draft.from > draft.to
        ? { from: draft.to, to: draft.from }
        : draft;
    onPeriodChange(range.from || range.to ? "custom" : "all", range);
    setSheetOpen(false);
  };

  const clearCustom = () => {
    onPeriodChange("all", { from: "", to: "" });
    setSheetOpen(false);
  };

  return (
    <S.Container>
      <S.SearchInput
        placeholder="Buscar por nome ou código"
        value={search}
        onChange={(e) => onSearchChange(e.target.value)}
        inputProps={{
          maxLength: 50,
          enterKeyHint: "search",
          "aria-label": "Buscar",
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon style={{ color: "#999999" }} />
            </InputAdornment>
          ),
          endAdornment: search ? (
            <InputAdornment position="end">
              <IconButton
                size="small"
                aria-label="Limpar busca"
                onClick={() => onSearchChange("")}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : undefined,
        }}
      />

      <S.ChipRow>
        {TYPE_OPTIONS.map((option) => (
          <S.Chip
            key={option.value}
            type="button"
            $active={type === option.value}
            onClick={(e) => {
              revealChip(e.currentTarget);
              onTypeChange(option.value);
            }}
          >
            {option.label}
          </S.Chip>
        ))}
      </S.ChipRow>

      <S.ChipRow>
        {PERIOD_OPTIONS.map((option) => (
          <S.Chip
            key={option.value}
            type="button"
            $active={period === option.value}
            onClick={(e) => {
              revealChip(e.currentTarget);
              onPeriodChange(option.value);
            }}
          >
            {option.label}
          </S.Chip>
        ))}
        <S.Chip type="button" $active={period === "custom"} onClick={openSheet}>
          <EventIcon style={{ fontSize: 16 }} />
          {period === "custom" ? customLabel(customRange) : "Personalizado"}
        </S.Chip>
      </S.ChipRow>

      {hasActiveFilters && (
        <S.ClearButton type="button" onClick={onClear}>
          Limpar filtros
        </S.ClearButton>
      )}

      <S.Sheet
        anchor="bottom"
        open={sheetOpen}
        onOpen={() => setSheetOpen(true)}
        onClose={() => setSheetOpen(false)}
        disableSwipeToOpen
      >
        <S.SheetHandle />
        <S.SheetTitle>Escolher período</S.SheetTitle>

        <S.DateRow>
          <S.DateField>
            De
            <input
              type="date"
              value={draft.from}
              max={draft.to || today}
              onChange={(e) => setDraft({ ...draft, from: e.target.value })}
            />
          </S.DateField>
          <S.DateField>
            Até
            <input
              type="date"
              value={draft.to}
              min={draft.from || undefined}
              max={today}
              onChange={(e) => setDraft({ ...draft, to: e.target.value })}
            />
          </S.DateField>
        </S.DateRow>

        <S.SheetActions>
          <S.SecondaryButton onClick={clearCustom}>Limpar</S.SecondaryButton>
          <S.PrimaryButton
            onClick={applyCustom}
            disabled={!draft.from && !draft.to}
          >
            Aplicar
          </S.PrimaryButton>
        </S.SheetActions>
      </S.Sheet>
    </S.Container>
  );
};

export default StatementFilters;
